import { useEffect } from 'react';
import { Box, Stack, Text } from 'braid-design-system';

export interface ToastProps {
  id: string;
  type: 'error' | 'warning' | 'success' | 'info';
  title: string;
  message?: string;
  duration?: number;
  stackIndex?: number;
  total?: number;
  isHovered?: boolean;
  onClose: (id: string) => void;
}

export const Toast = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  stackIndex = 0,
  isHovered = false,
  onClose,
}: ToastProps) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  const getToastStyles = () => {
    const baseStyles = {
      position: 'absolute' as const,
      top: '0px',
      right: '0px',
      width: '360px',
      padding: '16px',
      borderRadius: '12px',
      boxShadow: isHovered 
        ? '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 16px -6px rgba(0, 0, 0, 0.04)' 
        : stackIndex === 0 
          ? '0 12px 32px -4px rgba(0, 0, 0, 0.12), 0 8px 16px -6px rgba(0, 0, 0, 0.06)' 
          : '0 4px 12px rgba(0, 0, 0, 0.04)',
      border: '1px solid rgba(226, 232, 240, 0.8)',
      transition: 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.1), opacity 0.3s ease-in-out, box-shadow 0.3s',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      pointerEvents: (isHovered || stackIndex === 0) ? ('auto' as const) : ('none' as const),
    };

    // Calculate stack layout values
    const transformVal = isHovered 
      ? `translateY(${stackIndex * 78}px) scale(1)` 
      : `translateY(${stackIndex * 10}px) scale(${1 - stackIndex * 0.04})`;

    const opacityVal = isHovered 
      ? 1 
      : stackIndex >= 3 
        ? 0 
        : 1 - stackIndex * 0.25;

    const zIndexVal = 99999 - stackIndex;

    const typeStyles = {
      error: {
        backgroundColor: '#ffffff',
        borderLeft: '5px solid #ef4444',
        color: '#1e293b',
      },
      warning: {
        backgroundColor: '#ffffff',
        borderLeft: '5px solid #f59e0b',
        color: '#1e293b',
      },
      success: {
        backgroundColor: '#ffffff',
        borderLeft: '5px solid #10b981',
        color: '#1e293b',
      },
      info: {
        backgroundColor: '#ffffff',
        borderLeft: '5px solid #3b82f6',
        color: '#1e293b',
      },
    };

    return { 
      ...baseStyles, 
      ...typeStyles[type],
      transform: transformVal,
      opacity: opacityVal,
      zIndex: zIndexVal,
    };
  };

  return (
    <Box style={getToastStyles()}>
      <Stack space="xsmall">
        <Box display="flex" justifyContent="spaceBetween" alignItems="flexStart">
          <Box display="flex" alignItems="center">
            <Stack space="xsmall">
              <Text weight="strong" size="small">
                {title}
              </Text>
              {message ? (
                <Text size="small" tone="secondary">
                  {message}
                </Text>
              ) : null}
            </Stack>
          </Box>
          <Box flexShrink={0} paddingLeft="small">
            <button
              onClick={() => onClose(id)}
              style={{
                background: 'rgba(15, 23, 42, 0.05)',
                border: 'none',
                cursor: 'pointer',
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                color: '#64748b',
                padding: '0',
                lineHeight: 1,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                e.currentTarget.style.color = '#ef4444';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(15, 23, 42, 0.05)';
                e.currentTarget.style.color = '#64748b';
              }}
            >
              ×
            </button>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};
